// =============================================================
// HTML sanitization — defense-in-depth against XSS.
// =============================================================
//
// Admin-authored content (page bodies, nav labels, SEO overrides)
// passes through here before reaching the DOM. The admin is
// authenticated, but we don't want a stolen token or a future
// bug to be able to inject script into the site.

import DOMPurify from 'dompurify';

const config: Parameters<typeof DOMPurify.sanitize>[1] = {
  // NOTE: We do NOT use USE_PROFILES.svg here. When both USE_PROFILES
  // and a custom ALLOWED_ATTR are set, DOMPurify somehow drops the
  // SVG geometry attributes (viewBox, d, fill). Providing a custom
  // ALLOWED_ATTR alone (with the SVG attrs enumerated below) preserves
  // them correctly.
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'code', 'pre', 'blockquote',
    'hr', 'span', 'div',
    // Structural tags so the Main page (which wraps the entire home view
    // inside one body) keeps its hero/about/locations/etc. sections.
    'section', 'header', 'footer', 'main', 'aside', 'article',
    'figure', 'figcaption', 'dl', 'dt', 'dd', 'fieldset', 'legend',
    // Form controls so the contact form survives sanitization.
    'form', 'input', 'textarea', 'select', 'option', 'label', 'button',
    'small', 'sup', 'sub',
    // SVG so the icons in the home page render. Attribute allowlist
    // comes from USE_PROFILES.svg above (viewBox, d, fill, etc.).
    'svg', 'path', 'g', 'circle', 'rect', 'line', 'polyline', 'polygon',
    'use', 'defs', 'title', 'desc', 'marker', 'mask',
    // Images + media.
    'img', 'picture', 'source', 'video', 'audio',
    // Widget markers that admins drop into the page body. The renderer
    // expands <div data-widget="contact|agenda"></div> into real widget
    // HTML; sanitization lets the marker through and the strip-attrs
    // pass keeps the marker inert until expansion.
    'template',
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'rel', 'target', 'name', 'value', 'type',
    'id', 'class', 'role', 'aria-label', 'aria-labelledby', 'aria-controls',
    'aria-selected', 'aria-expanded', 'aria-hidden', 'aria-live',
    'aria-describedby', 'aria-modal', 'aria-current', 'aria-haspopup',
    'data-i18n', 'data-i18n-attr', 'data-nav', 'data-slug', 'data-counter',
    'data-target', 'data-tab', 'data-panel', 'data-loc', 'data-widget',
    'data-original-comment',
    'tabindex', 'for', 'minlength', 'required', 'autocomplete',
    'rows', 'cols', 'placeholder', 'min', 'max', 'step', 'pattern',
    'contenteditable', 'spellcheck', 'draggable',
    'loading', 'decoding', 'width', 'height', 'alt', 'src', 'srcset',
    // SVG attrs are covered by USE_PROFILES.svg, but list the ones we
    // actually use in the site so a future maintainer can audit them.
    'viewbox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap',
    'stroke-linejoin', 'd', 'xmlns', 'cx', 'cy', 'r', 'x', 'y',
    'points', 'x1', 'y1', 'x2', 'y2',
    'hidden', 'selected', 'checked', 'disabled', 'readonly',
    'multiple', 'accept', 'pattern', 'inputmode', 'maxlength',
    'enctype', 'method', 'action', 'novalidate',
  ],
  // NOTE: We deliberately do NOT set ALLOWED_URI_REGEXP. DOMPurify v3
  // applies that regex to every URI-valued attribute — including SVG
  // `fill` and `d` (which can technically hold `url(#…)` references).
  // A custom restrictive regex ends up stripping those SVG geometry
  // attributes. The library's default URI regex already rejects
  // `javascript:`, `data:`, and `vbscript:`, so we inherit it.
  // Force external links to be safe.
  ADD_ATTR: ['rel', 'target'],
  // Don't strip <section>, etc. when they have only an id attribute.
  KEEP_CONTENT: true,
};

// addHook is only available on a DOMPurify instance (the default export
// from 'dompurify' is a factory when no window is attached, e.g. in Node).
// We only call addHook when we have a real instance, otherwise the import
// would throw under any non-browser runtime that pulls this module in.
if (typeof (DOMPurify as { addHook?: unknown }).addHook === 'function') {
  (DOMPurify as { addHook: (name: string, fn: (node: Element) => void) => void })
    .addHook('afterSanitizeAttributes', (node) => {
      if (node instanceof HTMLAnchorElement) {
        const href = node.getAttribute('href') || '';
        if (/^https?:\/\//i.test(href)) {
          node.setAttribute('rel', 'noopener noreferrer');
          node.setAttribute('target', '_blank');
        }
      }
    });
}

export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, config) as unknown as string;
}